/**
 * The screen the user was last shown, remembered so UI events can carry it.
 *
 * `button_tapped`, `selection_made`, `list_item_selected` and `field_entered`
 * all require `screen_name` (catalog rule `one-location-property`), and their
 * call sites are imperative callbacks that have no idea what screen they are
 * on. Threading it through every one of them would work and would be wrong: the
 * value would be restated at ~50 sites and each restatement could drift from
 * the screen the app actually reported, silently, with no way to notice.
 *
 * So it is recorded once, where the truth already is — the moment
 * `screen_displayed` fires. A button is then labelled with the screen the user
 * was genuinely looking at when they tapped it, by construction rather than by
 * everyone remembering.
 *
 * A sheet or modal opened over a screen deliberately does not update this: it
 * reports no `screen_displayed` (it would double-count the screen underneath),
 * and a control inside it does belong to the screen it is covering.
 *
 * Module state rather than React context, because most callers are event
 * handlers rather than renders, and because a context above the router is a
 * remount risk this does not need to take.
 */
let currentScreen: string | null = null;

/** Records the screen that has just been displayed. */
export const setCurrentScreen = (screenName: string | null) => {
  currentScreen = screenName;
};

/**
 * The screen to attach to a UI event, or `null` before any screen has been
 * displayed — in which case the property is omitted rather than guessed.
 */
export const getCurrentScreen = () => currentScreen;

/**
 * Whether the interaction events are currently allowed to report a user action.
 *
 * `button_tapped` claims someone tapped something. Most navigation helpers are
 * shared between a real tap and a programmatic path — vas-v2 opens a story from
 * a card *and* from a push notification through the same function — so the
 * helper cannot know, and the caller can. This is off only inside
 * `withoutUiEvents`.
 */
let isUserInitiated = true;

export const getIsUserInitiated = () => isUserInitiated;

/**
 * Runs `navigate` with the universal interaction events suppressed, for a
 * navigation nobody tapped: a deep link, a push notification, a redirect.
 *
 * The legacy events still fire — they have always counted these, and
 * `alias-not-rename` says a migration does not quietly change what a live
 * series means. What this prevents is the *new* vocabulary inheriting the
 * problem: a notification launch landing in a button funnel, where it would be
 * indistinguishable from a tap and would inflate exactly the number the funnel
 * exists to measure.
 *
 * **Synchronous callbacks only.** The flag is restored when `navigate` returns,
 * so an `await` inside it resumes with reporting already back on. That errs
 * toward reporting too much rather than suppressing too much, which is the
 * cheaper direction to be wrong in — but it means this cannot wrap an async
 * flow.
 */
export const withoutUiEvents = <T>(navigate: () => T): T => {
  const previous = isUserInitiated;

  isUserInitiated = false;

  try {
    return navigate();
  } finally {
    isUserInitiated = previous;
  }
};
